/* Returned Parcels — Documents sub-panel (Iter 156).
 *
 * Ops workflow:
 *   1. Type/scan tracking → Fetch Parcel → invoice/client/customer/
 *      products auto-fill from Database A (last 12 months).
 *   2. Fill Return Date / Reason / Remarks / Received By.
 *   3. Save. Row appears in the table below and dashboard cards
 *      refresh.
 *
 * Visual language mirrors PendingParcelsPanel (KPI cards, table
 * grouping, toolbar, export dialog).
 */
import { RpMobileCards } from "@/components/RpMobileCards";
import { DbaScanDialog } from "@/components/DbaScanDialog";
import { useState, useEffect, useCallback, useRef } from "react";
import { ReturnValueStrip } from "@/components/ReturnValueStrip";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package, RotateCcw, Truck, Search, RefreshCw, Download, Printer,
  Trash2, Pencil, CheckCircle2, Clock, Calendar, TrendingUp,
  Copy, ChevronDown, ChevronUp, History, Users,
  Camera,
} from "lucide-react";
import { BilledToCell, BilledToGroupRow } from "@/components/BilledToCell";

const REASONS = [
  "Client Denied", "Client Unavailable", "Address Incorrect",
  "Delivery Refused", "Customs Return", "Returned to Sender", "Other",
];
// Iter 161 — status codes stored in DB. Display label is computed
// via `statusLabel()` so archived rows show MARG-<vr> / DMG-<vr> / EMP-<vr>.
const STATUSES = ["Returned", "Awaiting Repack", "Reshipped",
                  "MARG_OK", "DAMAGE", "EMPTY_PARCEL"];
const ARCHIVAL_STATUSES = ["MARG_OK", "DAMAGE", "EMPTY_PARCEL"];
const STATUS_LABELS = {
  "Returned": "Returned",
  "Awaiting Repack": "Awaiting Repack",
  "Reshipped": "Reshipped",
  "MARG_OK": "Marg OK",
  "DAMAGE": "Damage Parcel",
  "EMPTY_PARCEL": "Empty Parcel Received",
};
const STATUS_PREFIX = { MARG_OK: "MARG", DAMAGE: "DMG", EMPTY_PARCEL: "EMP" };
const statusLabel = (row) => {
  if (row && ARCHIVAL_STATUSES.includes(row.status) && row.voucher_no) {
    return `${STATUS_PREFIX[row.status]}-${row.voucher_no}`;
  }
  return STATUS_LABELS[row?.status] || row?.status || "";
};
const PAGE_SIZE = 100;

const emptyReturn = () => ({
  tracking: "", invoice_no: "", client_code: "", customer_name: "",
  service: "", destination_country: "", shipping_date: "",
  products: [], total_qty: 0, total_weight: 0,
  return_date: new Date().toISOString().slice(0, 10),
  return_reason: "", remarks: "", received_by: "",
  status: "Returned",
});

const KpiCard = ({ icon, label, value, tone, testid, subtitle }) => {
  const toneMap = {
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    rose:    'bg-rose-50 border-rose-200 text-rose-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone] || toneMap.slate}`}
         data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase opacity-80">
        {icon}<span>{label}</span>
      </div>
      <div className="mt-0.5 text-2xl font-bold">{value}</div>
      {subtitle && <div className="text-[10px] opacity-70">{subtitle}</div>}
    </div>
  );
};

export default function ReturnedParcelsPanel({ user }) {
  const isAdmin = user?.role === "admin";
  const trackingRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, returned: 0,
    awaiting_repack: 0, reshipped: 0, received_today: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kpiFilter, setKpiFilter] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  // Fetch/Register form
  const [draft, setDraft] = useState(emptyReturn());
  const [trackingInput, setTrackingInput] = useState("");
  const [fetching, setFetching] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [batch, setBatch] = useState({ open: false, reason: "", saved: [] });
  const batchSaved = useRef(0);
  const closeScan = useCallback(() => setScanOpen(false), []);
  const [saving, setSaving] = useState(false);
  const [dupPopup, setDupPopup] = useState(null);

  // Row inline editing (status/remarks)
  const [editRow, setEditRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Iter 157 — Bulk Return Upload dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Iter 157 — Reship-link dialog (per-row)
  const [reshipRow, setReshipRow] = useState(null);
  const [reshipTracking, setReshipTracking] = useState("");
  const [reshipInvoice, setReshipInvoice] = useState("");
  const [reshipDate, setReshipDate] = useState(new Date().toISOString().slice(0, 10));
  const [reshipSaving, setReshipSaving] = useState(false);

  // Iter 158 — Analytics card
  const [analytics, setAnalytics] = useState(null);
  // Iter 163 — folded by default; toggles open on chevron click.
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Iter 160 — Reship History modal
  const [historyRow, setHistoryRow] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Iter 161 — Archive view + voucher popup + dual date filters + archive KPIs
  const [viewMode, setViewMode] = useState("active");  // "active" | "archive"
  const [invoiceDateFrom, setInvoiceDateFrom] = useState("");
  const [invoiceDateTo, setInvoiceDateTo] = useState("");
  const [returnDateFrom, setReturnDateFrom] = useState("");
  const [returnDateTo, setReturnDateTo] = useState("");
  const [voucherFilter, setVoucherFilter] = useState("");
  const [voucherDialog, setVoucherDialog] = useState(null); // {row, targetStatus}
  const [voucherInput, setVoucherInput] = useState("");
  const [voucherSaving, setVoucherSaving] = useState(false);
  const [archiveStats, setArchiveStats] = useState({
    marg_ok: 0, damage: 0, empty: 0, today: 0, total: 0,
  });
  const [archiveKpiFilter, setArchiveKpiFilter] = useState("");
  // Iter 174 — sort + near-expiry preset
  const [sortBy, setSortBy] = useState("return_date");
  const [sortDir, setSortDir] = useState("desc");
  const [nearExpiry, setNearExpiry] = useState(false);
  const [groupBilled, setGroupBilled] = useState(false);
  const handleSortClick = (col) => {
    if (nearExpiry) setNearExpiry(false);
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir(["return_date", "invoice_date", "unit_inr", "unit_usd", "value"].includes(col) ? "desc" : "asc");
    }
  };
  const sortArrow = (col) =>
    (nearExpiry && col === "expiry") ? " ↑"
    : sortBy === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  // Iter 175 — auto-classify how close a batch is to expiry.
  //   "critical"  → within 90 days  (red pill)
  //   "warning"   → within 180 days (amber pill)
  //   "ok"        → later than 180 days
  //   ""          → unparseable / missing
  const expiryTone = (raw) => {
    if (!raw) return "";
    const now = new Date();
    let earliest = null;
    for (const tok of String(raw).split(/[,;]/).map(s => s.trim()).filter(Boolean)) {
      let d = null;
      let m = tok.match(/^(\d{1,2})[\/\-](\d{2,4})$/);
      if (m) {
        const mo = parseInt(m[1], 10);
        let yr = parseInt(m[2], 10);
        if (yr < 100) yr += 2000;
        // Use LAST day of month — batches expire at month-end.
        d = new Date(yr, mo, 0);
      } else if (/^\d{4}-\d{2}(-\d{2})?$/.test(tok)) {
        const [y, mo, dd] = tok.split('-').map(Number);
        d = new Date(y, mo - 1, dd || new Date(y, mo, 0).getDate());
      } else if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(tok)) {
        const [dd, mo, yr] = tok.split(/[\/\-]/).map(Number);
        d = new Date(yr < 100 ? yr + 2000 : yr, mo - 1, dd);
      }
      if (d && !isNaN(d)) {
        if (!earliest || d < earliest) earliest = d;
      }
    }
    if (!earliest) return "";
    const days = Math.round((earliest - now) / 86400000);
    if (days <= 90)  return "critical";
    if (days <= 180) return "warning";
    return "ok";
  };
  const expiryCellClass = (raw) => {
    const t = expiryTone(raw);
    if (t === "critical") return "bg-red-100 text-red-900 font-semibold";
    if (t === "warning")  return "bg-amber-100 text-amber-900 font-semibold";
    return "text-slate-600";
  };
  // Iter 163 — Password prompt for editing archived rows.
  // Populated when a save/archive/unarchive call returns 423.
  const [archivedPwdPrompt, setArchivedPwdPrompt] = useState(null);
  // Shape: { verb, retry }  — verb: 'edit' | 'change voucher' | 'restore'

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/returned-parcels/stats`,
        { headers: getAuthHeader() });
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  // Iter 161 — Archive KPI stats
  const fetchArchiveStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/returned-parcels/archive/stats`,
        { headers: getAuthHeader() });
      setArchiveStats(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (debouncedQ) params.search = debouncedQ;
      // Iter 161 — Active vs Archive view
      params.archived = viewMode === "archive" ? "true" : "false";
      if (voucherFilter.trim()) params.voucher_no = voucherFilter.trim();
      if (returnDateFrom) params.date_from = returnDateFrom;
      if (returnDateTo)   params.date_to   = returnDateTo;
      if (invoiceDateFrom) params.invoice_date_from = invoiceDateFrom;
      if (invoiceDateTo)   params.invoice_date_to   = invoiceDateTo;
      // Iter 174 — sort + near-expiry
      params.sort_by = sortBy;
      params.sort_dir = sortDir;
      if (nearExpiry) params.near_expiry = true;
      // Archive KPI filters override status when in archive view.
      if (viewMode === "archive" && archiveKpiFilter) {
        params.status = archiveKpiFilter;
      } else if (kpiFilter === "returned") params.status = "Returned";
      else if (kpiFilter === "awaiting") params.status = "Awaiting Repack";
      else if (kpiFilter === "reshipped") params.status = "Reshipped";
      else if (kpiFilter === "today") params.received_today = true;
      else if (statusFilter) params.status = statusFilter;
      const r = await axios.get(`${API}/returned-parcels`,
        { headers: getAuthHeader(), params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load returned parcels");
    } finally { setLoading(false); }
  }, [debouncedQ, statusFilter, kpiFilter, page, viewMode, voucherFilter,
      returnDateFrom, returnDateTo, invoiceDateFrom, invoiceDateTo,
      archiveKpiFilter, sortBy, sortDir, nearExpiry]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchArchiveStats(); }, [fetchArchiveStats, viewMode]);
  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [debouncedQ, statusFilter, kpiFilter,
                                     viewMode, archiveKpiFilter, voucherFilter,
                                     returnDateFrom, returnDateTo,
                                     invoiceDateFrom, invoiceDateTo]);

  // Iter 158 — fetch top return reasons per client (30 day window).
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const r = await axios.get(`${API}/returned-parcels/analytics/top-reasons`,
        { headers: getAuthHeader(), params: { days: 30, limit: 8 } });
      setAnalytics(r.data);
    } catch { /* silent — card just hides */ }
    finally { setAnalyticsLoading(false); }
  }, []);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics, stats.total]);

  const fetchParcel = async (tkArg) => {
    const tk = (typeof tkArg === "string" ? tkArg : trackingInput).trim().toUpperCase();
    if (!tk) { toast.error("Enter a tracking number first"); return false; }
    if (typeof tkArg === "string") setTrackingInput(tk);
    setFetching(true);
    try {
      const r = await axios.post(`${API}/returned-parcels/lookup`,
        { tracking: tk }, { headers: getAuthHeader() });
      if (r.data.duplicate) {
        setDupPopup({ tracking: tk, existing: r.data.existing });
        return true;
      }
      if (!r.data.found) {
        toast.error(`Tracking ${tk} not found in Database A (last 12 months)`);
        return false;
      }
      setDraft(draftFrom(r.data.parcel));
      toast.success(`Loaded ${r.data.parcel.products?.length || 0} product(s) for ${tk}`);
      setTimeout(() => document.querySelector('[data-testid="rp-quick-reasons"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
      return false;
    } finally { setFetching(false); }
  };

  const draftFrom = (p) => ({
        ...emptyReturn(),
        tracking: p.tracking,
        invoice_no: p.invoice_no || "",
        client_code: p.client_code || "",
        customer_name: p.customer_name || "",
        service: p.service || "",
        shipping_date: p.shipping_date || "",
        destination_country: p.destination_country || "",
        products: p.products || [],
        total_qty: p.total_qty || 0,
        total_weight: p.total_weight || 0,
        return_date: new Date().toISOString().slice(0, 10),
        received_by: user?.name || user?.email || "",
  });

  // Back-to-back scanning: one reason for the pile, each scan saves straight away.
  const batchCode = async (tk) => {
    if (!batch.reason) return { ok: false, msg: "Pick a return reason above first" };
    try {
      const r = await axios.post(`${API}/returned-parcels/lookup`, { tracking: tk }, { headers: getAuthHeader() });
      if (r.data.duplicate) return { ok: false, msg: `${tk} is already registered` };
      if (!r.data.found) return { ok: false, msg: `${tk} not found in Database A` };
      await axios.post(`${API}/returned-parcels`, { ...draftFrom(r.data.parcel), return_reason: batch.reason }, { headers: getAuthHeader() });
      batchSaved.current += 1;
      setBatch(b => ({ ...b, saved: [tk, ...b.saved] }));
      return { ok: true, msg: `Saved ${tk} — scan the next parcel` };
    } catch (e) { return { ok: false, msg: e.response?.data?.detail || `Save failed for ${tk}` }; }
  };
  const closeBatch = useCallback(() => {
    if (batchSaved.current) toast.success(`Batch scan: ${batchSaved.current} returned parcel(s) registered`);
    batchSaved.current = 0;
    setBatch({ open: false, reason: "", saved: [] });
    fetchStats(); fetchRows();
  }, [fetchStats, fetchRows]);

  const saveReturn = async (reasonArg) => {
    const body = typeof reasonArg === "string" ? { ...draft, return_reason: reasonArg } : draft;
    if (!body.tracking) { toast.error("Fetch a parcel first"); return; }
    if (!body.return_reason) { toast.error("Select a return reason"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/returned-parcels`, body,
        { headers: getAuthHeader() });
      toast.success(`Returned parcel ${body.tracking} registered`);
      setDraft(emptyReturn());
      setTrackingInput("");
      trackingRef.current?.focus();
      fetchStats(); fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const inlineUpdate = async (row, patch, password = "") => {
    try {
      const body = password ? { ...patch, password } : patch;
      await axios.put(`${API}/returned-parcels/${row.id}`, body,
        { headers: getAuthHeader() });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...patch } : r));
      toast.success("Updated");
      fetchStats();
      return true;
    } catch (e) {
      // Iter 163 — prompt for the operator's own password when the
      // server signals the row is archived (423).
      if (e.response?.status === 423) {
        setArchivedPwdPrompt({
          verb: 'edit',
          retry: (pwd) => inlineUpdate(row, patch, pwd),
        });
        return false;
      }
      toast.error(e.response?.data?.detail || "Update failed");
      return false;
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/returned-parcels/${confirmDelete.id}/delete`,
        { password: deletePassword }, { headers: getAuthHeader() });
      toast.success("Deleted");
      setConfirmDelete(null);
      setDeletePassword("");
      fetchStats(); fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const exportXlsx = async () => {
    try {
      const params = {};
      if (debouncedQ) params.search = debouncedQ;
      if (statusFilter) params.status = statusFilter;
      // Iter 174 — mirror view state so the sheet matches the screen.
      params.sort_by = sortBy;
      params.sort_dir = sortDir;
      if (nearExpiry) params.near_expiry = true;
      params.archived = viewMode === "archive" ? "true" : "false";
      if (voucherFilter.trim()) params.voucher_no = voucherFilter.trim();
      if (returnDateFrom) params.date_from = returnDateFrom;
      if (returnDateTo)   params.date_to   = returnDateTo;
      if (invoiceDateFrom) params.invoice_date_from = invoiceDateFrom;
      if (invoiceDateTo)   params.invoice_date_to   = invoiceDateTo;
      const r = await axios.get(`${API}/returned-parcels/export`,
        { headers: getAuthHeader(), params, responseType: "blob" });
      // filename from CD header
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : "Returned-Parcels.xlsx";
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  // Iter 157 — Bulk Return Upload: paste/upload tracking numbers,
  // pick a common reason + date, register everything in one pass.
  const runBulkUpload = async () => {
    const raw = bulkText.trim();
    if (!raw) { toast.error("Paste at least one tracking number"); return; }
    if (!bulkReason) { toast.error("Pick a return reason"); return; }
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const r = await axios.post(`${API}/returned-parcels/bulk-upload`,
        {
          trackings: raw,
          return_reason: bulkReason,
          return_date: bulkDate,
          remarks: bulkRemarks,
        }, { headers: getAuthHeader() });
      setBulkResult(r.data);
      const c = (r.data.created || []).length;
      const d = (r.data.duplicates || []).length;
      const nf = (r.data.not_found || []).length;
      toast.success(`Bulk done — created ${c}, duplicates ${d}, not found ${nf}`);
      fetchStats(); fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const onCsvUpload = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      // Strip headers/quotes and keep the first column when the file
      // is a proper CSV with columns; otherwise treat as raw list.
      const lines = text.split(/[\r\n]+/).filter(Boolean);
      const cleaned = lines.map(l => l.split(",")[0].replace(/["']/g, "").trim());
      // Skip a "tracking"-header row if present.
      if (cleaned[0] && /^tracking(_?(number|no))?$/i.test(cleaned[0])) cleaned.shift();
      setBulkText(cleaned.join("\n"));
      toast.success(`Loaded ${cleaned.length} tracking(s) from ${file.name}`);
    } catch { toast.error("Could not read CSV file"); }
  };

  // Iter 157 — Reship link: set status=Reshipped, capture new
  // tracking/invoice/date and store them on the return record.
  const openReship = (row) => {
    setReshipRow(row);
    setReshipTracking(row.new_tracking || "");
    setReshipInvoice(row.new_invoice || "");
    setReshipDate(row.reship_date || new Date().toISOString().slice(0, 10));
  };
  const submitReship = async () => {
    if (!reshipTracking.trim()) { toast.error("New tracking is required"); return; }
    if (!reshipInvoice.trim()) { toast.error("New invoice is required"); return; }
    setReshipSaving(true);
    try {
      const r = await axios.post(
        `${API}/returned-parcels/${reshipRow.id}/reship`,
        { new_tracking: reshipTracking.trim(),
          new_invoice: reshipInvoice.trim().toUpperCase(),
          reship_date: reshipDate },
        { headers: getAuthHeader() });
      setRows(prev => prev.map(x => x.id === reshipRow.id ? r.data : x));
      toast.success(`Linked to new tracking ${r.data.new_tracking}`);
      setReshipRow(null); fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reship link failed");
    } finally { setReshipSaving(false); }
  };

  // Iter 158 — Clone a Reshipped row into the Bulk Data flow.
  // We serialise the parcel (with the *new* tracking + invoice) into
  // both (a) a tab-separated row on the clipboard for the ops user
  // to paste into any spreadsheet-style grid, and (b) sessionStorage
  // so a future Bulk Data prefill hook can consume the same payload.
  // Then we deep-link to `?panel=doc_bulk_data`.
  const cloneToBulkData = async (row) => {
    if (row.status !== 'Reshipped' || !row.new_tracking) {
      toast.error("Only Reshipped rows with a new tracking can be cloned");
      return;
    }
    const products = row.products?.length ? row.products : [];
    const payload = {
      source: 'returned_parcels',
      original_tracking: row.tracking,
      original_invoice: row.invoice_no,
      new_tracking: row.new_tracking,
      new_invoice: row.new_invoice,
      reship_date: row.reship_date,
      client_code: row.client_code,
      customer_name: row.customer_name,
      service: row.service,
      destination_country: row.destination_country,
      products,
      total_qty: row.total_qty,
      total_weight: row.total_weight,
    };
    try {
      sessionStorage.setItem('rp_clone_draft', JSON.stringify(payload));
    } catch { /* ignore quota errors */ }
    // Build a tab-separated row (or one row per product) so the
    // clipboard content pastes cleanly into Excel-style grids.
    const rows = products.length
      ? products.map(p => [
          row.new_tracking, row.new_invoice, row.client_code,
          row.customer_name, row.service, row.destination_country,
          p.name || '', p.qty ?? '', p.weight ?? '',
        ].join('\t'))
      : [[row.new_tracking, row.new_invoice, row.client_code,
           row.customer_name, row.service, row.destination_country,
           '', row.total_qty ?? '', row.total_weight ?? ''].join('\t')];
    const tsv = rows.join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success(
        `Row copied — paste (⌘V / Ctrl+V) into the Bulk Data grid`,
        { duration: 6000 });
    } catch {
      toast.info("Clipboard blocked — payload saved to session storage");
    }
    // Deep-link to Bulk Data via Dashboard's panelchange event.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('panel', 'doc_bulk_data');
      window.history.pushState({}, '', url.toString());
      window.dispatchEvent(new Event('panelchange'));
    } catch { /* SPA nav fallback */ window.location.hash = '#doc_bulk_data'; }
  };

  // Iter 160 — Open Reship History timeline modal for a row.
  const openHistory = async (row) => {
    setHistoryRow(row);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/returned-parcels/${row.id}/history`,
        { headers: getAuthHeader() });
      setHistoryData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load history");
      setHistoryRow(null);
    } finally { setHistoryLoading(false); }
  };

  // Iter 161 — Submit the Marg-OK / Damage / Empty Parcel voucher #
  // and move the row into Archive. Iter 163 — supports password retry
  // when the row is already archived (e.g. changing the voucher #).
  const submitVoucher = async (password = "") => {
    const vr = voucherInput.trim();
    if (!vr) { toast.error("Voucher number is required"); return; }
    setVoucherSaving(true);
    try {
      const { row, targetStatus } = voucherDialog;
      const body = { status: targetStatus, voucher_no: vr };
      if (password) body.password = password;
      const r = await axios.post(
        `${API}/returned-parcels/${row.id}/archive`,
        body, { headers: getAuthHeader() });
      const shortLbl = statusLabel(r.data);
      toast.success(`Archived — ${shortLbl}`);
      setVoucherDialog(null);
      setVoucherInput("");
      fetchStats(); fetchArchiveStats(); fetchRows();
    } catch (e) {
      if (e.response?.status === 423) {
        setArchivedPwdPrompt({
          verb: 'change voucher',
          retry: (pwd) => submitVoucher(pwd),
        });
      } else {
        toast.error(e.response?.data?.detail || "Archive failed");
      }
    } finally { setVoucherSaving(false); }
  };

  // Iter 161 — Un-archive: pop a row back into the active Returned list.
  // Iter 163 — password guarded.
  const unarchiveRow = async (row, password = "") => {
    try {
      const body = password ? { password } : {};
      await axios.post(`${API}/returned-parcels/${row.id}/unarchive`,
        body, { headers: getAuthHeader() });
      toast.success("Restored to Active");
      fetchStats(); fetchArchiveStats(); fetchRows();
    } catch (e) {
      if (e.response?.status === 423) {
        setArchivedPwdPrompt({
          verb: 'restore',
          retry: (pwd) => unarchiveRow(row, pwd),
        });
        return;
      }
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };

  const screenPrint = () => {
    if (rows.length === 0) { toast.info("No rows to print"); return; }
    const esc = (s) => String(s ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const stamp = new Date().toLocaleString();
    // Iter 174 — one row per product with Batch + Expiry; header cells
    // stay in the first product row of each parcel.
    const productRows = (r) => {
      const prods = (r.products || []).length ? r.products : [{name:'—', qty:r.total_qty||0}];
      return prods.map((p, i) => `
        <tr class="prod-row ${i === 0 ? 'first' : ''}">
          <td>${i===0 ? esc(r.return_date) : ''}</td>
          <td>${i===0 ? esc((r.shipping_date||'').split('T')[0]) : ''}</td>
          <td>${i===0 ? esc(r.tracking) : ''}</td>
          <td>${i===0 ? esc(r.invoice_no || '') : ''}</td>
          <td>${i===0 ? esc(r.client_code) : ''}</td>
          <td>${i===0 ? esc(r.customer_name) : ''}</td>
          <td>${i===0 ? esc(r.destination_country || '') : ''}</td>
          <td>${i===0 ? esc(r.billed_to || '') : ''}</td>
          <td>${i===0 ? esc(r.service) : ''}</td>
          <td>${esc(p.name)} <span style="color:#666">×${p.qty || 0}</span></td>
          <td>${esc(p.batch || '')}</td>
          <td>${esc(p.exp || '')}</td>
        </tr>`).join('');
    };
    const trs = rows.map(productRows).join('');
    const title = (nearExpiry ? 'Near-Expiry — ' : '') +
                  (viewMode === 'archive' ? 'Archived Returns' : 'Returned Parcels');
    const html = `<!doctype html><html><head><title>${title} — ${stamp}</title>
      <style>
        body{font:12px/1.35 Arial,Helvetica,sans-serif;padding:14px}
        h1{font-size:14px;margin:0 0 6px}
        .meta{color:#666;font-size:10px;margin-bottom:10px}
        table{width:100%;border-collapse:collapse;font-size:10px}
        th,td{border:1px solid #999;padding:4px 6px;text-align:left;vertical-align:top}
        th{background:#fbeadc}
        tr.first td{border-top:2px solid #555}
        @media print { body{padding:8px} }
      </style></head><body>
      <h1>${title} Report</h1>
      <div class="meta">${rows.length} parcels • printed ${stamp}${nearExpiry ? ' • sorted by earliest expiry' : ''}</div>
      <table><thead><tr>
        <th>Return Date</th><th>Invoice Date</th><th>Tracking</th><th>Invoice #</th>
        <th>Client Code</th><th>Customer</th><th>Country</th><th>Billed To</th><th>Service</th>
        <th>Product</th><th>Batch</th><th>Expiry</th>
      </tr></thead><tbody>${trs}</tbody></table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),200)};</script>
      </body></html>`;
    const w = window.open('', '_blank', 'noopener,noreferrer,width=1200,height=800');
    if (!w) { toast.error("Pop-up blocked — please allow pop-ups"); return; }
    w.document.write(html); w.document.close();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4 p-4" data-testid="returned-parcels-panel">
      {/* Iter 161 — Active / Archive tab toggle */}
      <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden text-xs font-semibold"
           data-testid="rp-view-tabs">
        <button type="button"
          onClick={() => { setViewMode('active'); setArchiveKpiFilter(''); }}
          className={`px-3 py-1.5 ${viewMode === 'active'
            ? 'bg-amber-500 text-white' : 'text-slate-700 hover:bg-amber-50'}`}
          data-testid="rp-tab-active">
          Active Returned
        </button>
        <button type="button"
          onClick={() => { setViewMode('archive'); setKpiFilter(''); }}
          className={`px-3 py-1.5 ${viewMode === 'archive'
            ? 'bg-slate-700 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
          data-testid="rp-tab-archive">
          Archive
        </button>
      </div>

      {/* KPI cards — mirror Pending Parcels look, clickable filters */}
      {viewMode === 'active' ? (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button type="button"
          onClick={() => setKpiFilter(v => v === "returned" ? "" : "returned")}
          className={`text-left rounded-lg transition ${kpiFilter === 'returned' ? 'ring-2 ring-amber-400' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="rp-kpi-returned-btn">
          <KpiCard icon={<Package className="w-4 h-4" />} label="Returned Parcels"
            value={stats.returned} tone="amber" testid="rp-kpi-returned"
            subtitle="Active returned" />
        </button>
        <button type="button"
          onClick={() => setKpiFilter(v => v === "awaiting" ? "" : "awaiting")}
          className={`text-left rounded-lg transition ${kpiFilter === 'awaiting' ? 'ring-2 ring-rose-400' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="rp-kpi-awaiting-btn">
          <KpiCard icon={<Clock className="w-4 h-4" />} label="Awaiting Repack"
            value={stats.awaiting_repack} tone="rose" testid="rp-kpi-awaiting"
            subtitle="Waiting for processing" />
        </button>
        <button type="button"
          onClick={() => setKpiFilter(v => v === "reshipped" ? "" : "reshipped")}
          className={`text-left rounded-lg transition ${kpiFilter === 'reshipped' ? 'ring-2 ring-emerald-400' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="rp-kpi-reshipped-btn">
          <KpiCard icon={<Truck className="w-4 h-4" />} label="Reshipped"
            value={stats.reshipped} tone="emerald" testid="rp-kpi-reshipped"
            subtitle="Linked to new shipment" />
        </button>
        <button type="button"
          onClick={() => setKpiFilter(v => v === "today" ? "" : "today")}
          className={`text-left rounded-lg transition ${kpiFilter === 'today' ? 'ring-2 ring-indigo-400' : 'hover:ring-1 hover:ring-slate-200'}`}
          data-testid="rp-kpi-today-btn">
          <KpiCard icon={<Calendar className="w-4 h-4" />} label="Received Today"
            value={stats.received_today} tone="indigo" testid="rp-kpi-today"
            subtitle="Registered today" />
        </button>
      </div>
      ) : (
        // Iter 161 — Archive dashboard cards
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rp-archive-kpis">
          <button type="button"
            onClick={() => setArchiveKpiFilter(v => v === 'MARG_OK' ? '' : 'MARG_OK')}
            className={`text-left rounded-lg transition ${archiveKpiFilter === 'MARG_OK' ? 'ring-2 ring-emerald-400' : 'hover:ring-1 hover:ring-slate-200'}`}
            data-testid="rp-akpi-marg-btn">
            <KpiCard icon={<CheckCircle2 className="w-4 h-4" />} label="Marg OK"
              value={archiveStats.marg_ok} tone="emerald" testid="rp-akpi-marg"
              subtitle="Total archived" />
          </button>
          <button type="button"
            onClick={() => setArchiveKpiFilter(v => v === 'DAMAGE' ? '' : 'DAMAGE')}
            className={`text-left rounded-lg transition ${archiveKpiFilter === 'DAMAGE' ? 'ring-2 ring-rose-400' : 'hover:ring-1 hover:ring-slate-200'}`}
            data-testid="rp-akpi-dmg-btn">
            <KpiCard icon={<Trash2 className="w-4 h-4" />} label="Damage"
              value={archiveStats.damage} tone="rose" testid="rp-akpi-dmg"
              subtitle="Total damaged" />
          </button>
          <button type="button"
            onClick={() => setArchiveKpiFilter(v => v === 'EMPTY_PARCEL' ? '' : 'EMPTY_PARCEL')}
            className={`text-left rounded-lg transition ${archiveKpiFilter === 'EMPTY_PARCEL' ? 'ring-2 ring-amber-400' : 'hover:ring-1 hover:ring-slate-200'}`}
            data-testid="rp-akpi-emp-btn">
            <KpiCard icon={<Package className="w-4 h-4" />} label="Empty Parcel"
              value={archiveStats.empty} tone="amber" testid="rp-akpi-emp"
              subtitle="Total empty received" />
          </button>
          <div>
            <KpiCard icon={<Calendar className="w-4 h-4" />} label="Today Archived"
              value={archiveStats.today} tone="indigo" testid="rp-akpi-today"
              subtitle="Daily count" />
          </div>
        </div>
      )}
      {viewMode === 'active' && kpiFilter && (
        <div className="flex items-center gap-2 text-xs text-slate-600 -mt-1">
          <span className="font-medium">Filtered by KPI:</span>
          <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
            {kpiFilter === 'returned' ? 'Returned'
              : kpiFilter === 'awaiting' ? 'Awaiting Repack'
              : kpiFilter === 'reshipped' ? 'Reshipped'
              : 'Received Today'}
          </span>
          <button className="underline text-slate-500 hover:text-slate-800"
                  onClick={() => setKpiFilter("")} data-testid="rp-kpi-clear">
            clear
          </button>
        </div>
      )}

      {/* Iter 161 — Dual date filters + voucher search */}
      <div className="flex flex-wrap items-end gap-2 text-xs" data-testid="rp-date-filters">
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Return Date From</Label>
          <Input type="date" value={returnDateFrom} className="h-8 w-40"
                 onChange={(e) => setReturnDateFrom(e.target.value)}
                 data-testid="rp-flt-rdate-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Return Date To</Label>
          <Input type="date" value={returnDateTo} className="h-8 w-40"
                 onChange={(e) => setReturnDateTo(e.target.value)}
                 data-testid="rp-flt-rdate-to" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Invoice Date From</Label>
          <Input type="date" value={invoiceDateFrom} className="h-8 w-40"
                 onChange={(e) => setInvoiceDateFrom(e.target.value)}
                 data-testid="rp-flt-idate-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Invoice Date To</Label>
          <Input type="date" value={invoiceDateTo} className="h-8 w-40"
                 onChange={(e) => setInvoiceDateTo(e.target.value)}
                 data-testid="rp-flt-idate-to" />
        </div>
        {viewMode === 'archive' && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Voucher #</Label>
            <Input value={voucherFilter} className="h-8 w-32"
                   placeholder="e.g. 2345"
                   onChange={(e) => setVoucherFilter(e.target.value)}
                   data-testid="rp-flt-voucher" />
          </div>
        )}
        {(returnDateFrom || returnDateTo || invoiceDateFrom || invoiceDateTo || voucherFilter) && (
          <Button size="sm" variant="ghost" className="h-8 text-[11px]"
                  onClick={() => {
                    setReturnDateFrom(""); setReturnDateTo("");
                    setInvoiceDateFrom(""); setInvoiceDateTo("");
                    setVoucherFilter("");
                  }}
                  data-testid="rp-flt-clear">Clear filters</Button>
        )}
      </div>

      {/* Iter 158 — Return Analytics: top return reasons per client (30 d). */}
      {analytics && analytics.top_clients?.length > 0 && (
        <Card className="border-slate-200" data-testid="rp-analytics-card">
          <CardContent className="p-3">
            <button type="button"
                    onClick={() => setAnalyticsOpen(v => !v)}
                    className="w-full flex items-center gap-2 text-left"
                    data-testid="rp-analytics-toggle">
              <TrendingUp className="w-4 h-4 text-rose-600" />
              <span className="text-sm font-semibold text-slate-700">
                Top Return Reasons — Last 30 Days
              </span>
              <span className="text-[11px] text-slate-500">
                {analytics.total_returns} return{analytics.total_returns === 1 ? '' : 's'} across {analytics.top_clients.length} client{analytics.top_clients.length === 1 ? '' : 's'}
              </span>
              <span className="ml-auto text-slate-400">
                {analyticsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </button>
            {analyticsOpen && (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs border-collapse"
                       data-testid="rp-analytics-table">
                  <thead>
                    <tr className="bg-rose-50/60 text-rose-900 text-[10px] uppercase tracking-wide">
                      <th className="p-1.5 text-left border border-rose-100">Client</th>
                      <th className="p-1.5 text-left border border-rose-100">Customer</th>
                      <th className="p-1.5 text-left border border-rose-100">Top Reason</th>
                      <th className="p-1.5 text-right border border-rose-100 w-16">Total</th>
                      <th className="p-1.5 text-left border border-rose-100">Breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_clients.map((c, i) => (
                      <tr key={c.client_code + i}
                          className={i % 2 === 0 ? '' : 'bg-slate-50/40'}
                          data-testid={`rp-analytics-row-${c.client_code}`}>
                        <td className="p-1.5 font-mono font-semibold border border-slate-200">
                          {c.client_code}
                        </td>
                        <td className="p-1.5 border border-slate-200">{c.customer_name}</td>
                        <td className="p-1.5 border border-slate-200">
                          <span className="inline-block px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px] font-semibold">
                            {c.top_reason}
                          </span>
                        </td>
                        <td className="p-1.5 text-right border border-slate-200 font-semibold">
                          {c.total}
                        </td>
                        <td className="p-1.5 border border-slate-200">
                          <div className="flex flex-wrap gap-1">
                            {c.reasons.map(r => {
                              const pct = c.total ? Math.round((r.count / c.total) * 100) : 0;
                              return (
                                <span key={r.reason}
                                      className="inline-flex items-center gap-1 px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px]"
                                      title={`${r.count} of ${c.total} returns (${pct}%)`}>
                                  {r.reason}
                                  <span className="font-mono font-semibold">{r.count}</span>
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-[10px] text-slate-500">
                  Overall mix:{' '}
                  {analytics.overall_reason_mix.map((r, i) => (
                    <span key={r.reason}>
                      {i > 0 ? ' • ' : ''}
                      <span className="font-semibold">{r.reason}</span>
                      <span className="font-mono"> {r.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Fetch + Register card */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[280px]">
              <RotateCcw className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-slate-700">Register Returned Parcel</span>
              <span className="text-[10px] text-slate-400">— tracking-only lookup, 12-month window</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input ref={trackingRef}
                   value={trackingInput}
                   onChange={(e) => setTrackingInput(e.target.value.toUpperCase())}
                   onKeyDown={(e) => { if (e.key === 'Enter') fetchParcel(); }}
                   placeholder="Scan or type tracking number"
                   className="h-9 w-64 font-mono uppercase"
                   data-testid="rp-tracking-input" />
            <Button size="sm" onClick={() => fetchParcel()} disabled={fetching}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="rp-fetch-btn">
              {fetching ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Fetch Parcel
            </Button>
            <Button size="sm" variant="outline" onClick={() => setScanOpen(true)}
                    className="border-amber-400 text-amber-800" data-testid="rp-scan-btn">
              <Camera className="w-4 h-4 mr-1" /> Scan
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBatch({ open: true, reason: "", saved: [] })}
                    className="border-amber-400 text-amber-800" data-testid="rp-batch-scan-btn">
              <Camera className="w-4 h-4 mr-1" /> Batch scan
            </Button>
            {draft.tracking && (
              <span className="text-[11px] text-slate-500">
                Auto-fetched: <b>{draft.tracking}</b> • {draft.customer_name} • {draft.client_code}
              </span>
            )}
          </div>

          {draft.tracking && (
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60 space-y-3">
              <div className="flex flex-wrap items-center gap-1.5" data-testid="rp-quick-reasons">
                <span className="text-[11px] font-semibold text-slate-600 mr-1">Tap reason to save:</span>
                {REASONS.map(rs => (
                  <button key={rs} type="button" disabled={saving} onClick={() => saveReturn(rs)}
                    data-testid={`rp-quick-reason-${rs.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-50 active:bg-amber-100 disabled:opacity-50">
                    {rs}</button>))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><Label className="text-[10px] text-slate-500">Invoice #</Label>
                  <div className="font-mono font-semibold text-slate-800">{draft.invoice_no || '—'}</div>
                </div>
                <div><Label className="text-[10px] text-slate-500">Client Code</Label>
                  <div className="font-semibold text-slate-800">{draft.client_code || '—'}</div>
                </div>
                <div><Label className="text-[10px] text-slate-500">Customer</Label>
                  <div className="font-semibold text-slate-800">{draft.customer_name || '—'}</div>
                </div>
                <div><Label className="text-[10px] text-slate-500">Service</Label>
                  <div className="font-semibold text-slate-800">{draft.service || '—'}</div>
                </div>
                <div><Label className="text-[10px] text-slate-500">Destination</Label>
                  <div className="text-slate-800">{draft.destination_country || '—'}</div>
                </div>
                <div><Label className="text-[10px] text-slate-500">Shipping Date</Label>
                  <div className="text-slate-800">{(draft.shipping_date || '').slice(0, 10) || '—'}</div>
                </div>
                <div><Label className="text-[10px] text-slate-500">Products</Label>
                  <div className="text-slate-800">{draft.products?.length || 0} item(s), qty {draft.total_qty}</div>
                </div>
              </div>

              {/* Products list — grouped like Pending Parcels */}
              {draft.products?.length > 0 && (
                <div className="border border-slate-200 rounded bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-100 text-slate-600 text-[9px] uppercase">
                      <tr>
                        <th className="p-1.5 text-left border-b">Product</th>
                        <th className="p-1.5 text-right border-b w-16">Qty</th>
                        <th className="p-1.5 text-right border-b w-20">Unit ₹</th>
                        <th className="p-1.5 text-right border-b w-20">Unit $</th>
                        <th className="p-1.5 text-right border-b w-20">Wt (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.products.map((p, i) => (
                        <tr key={i} className="border-b last:border-b-0">
                          <td className="p-1.5">{p.name}</td>
                          <td className="p-1.5 text-right font-semibold">{p.qty || 0}</td>
                          <td className="p-1.5 text-right" data-testid={`rp-draft-price-inr-${i}`}>{p.price_inr != null ? `₹${Number(p.price_inr).toFixed(2)}` : ''}</td>
                          <td className="p-1.5 text-right text-emerald-700" data-testid={`rp-draft-price-usd-${i}`}>{p.price_usd != null ? `$${Number(p.price_usd).toFixed(2)}` : ''}</td>
                          <td className="p-1.5 text-right">{p.weight || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Return Date *</Label>
                  <Input type="date" value={draft.return_date}
                         onChange={(e) => setDraft(d => ({ ...d, return_date: e.target.value }))}
                         className="h-8 text-xs"
                         data-testid="rp-return-date" />
                </div>
                <div>
                  <Label className="text-xs">Return Reason *</Label>
                  <Select value={draft.return_reason || "__none__"}
                          onValueChange={(v) => setDraft(d => ({ ...d, return_reason: v === "__none__" ? "" : v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="rp-reason">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— select —</SelectItem>
                      {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Received By</Label>
                  <Input value={draft.received_by}
                         onChange={(e) => setDraft(d => ({ ...d, received_by: e.target.value }))}
                         placeholder="Operator name"
                         className="h-8 text-xs"
                         data-testid="rp-received-by" />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={draft.status}
                          onValueChange={(v) => setDraft(d => ({ ...d, status: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="rp-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 md:col-span-4">
                  <Label className="text-xs">Remarks</Label>
                  <Input value={draft.remarks}
                         onChange={(e) => setDraft(d => ({ ...d, remarks: e.target.value }))}
                         placeholder="Any notes about the return"
                         className="h-8 text-xs"
                         data-testid="rp-remarks" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setDraft(emptyReturn()); setTrackingInput(""); }}
                        data-testid="rp-cancel-draft">Clear</Button>
                <Button size="sm" onClick={saveReturn} disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="rp-save-return">
                  {saving ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving…</> :
                            <><CheckCircle2 className="w-3 h-3 mr-1" /> Save Return</>}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Toolbar + table */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search tracking, invoice, customer, billed to, product, batch…"
                     className="pl-8 h-9" data-testid="rp-search" />
            </div>
            <Select value={statusFilter || "__all__"}
                    onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-44 h-9" data-testid="rp-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm"
                    variant={nearExpiry ? "default" : "outline"}
                    onClick={() => { setNearExpiry(v => !v); setSortBy("expiry"); setSortDir("asc"); }}
                    className={nearExpiry
                      ? "bg-amber-600 hover:bg-amber-700 text-white"
                      : "border-amber-400 text-amber-700 hover:bg-amber-50"}
                    data-testid="rp-near-expiry-btn"
                    title="Sort parcels so earliest-expiring products appear first">
              <Clock className="w-4 h-4 mr-1" /> {nearExpiry ? "Near Expiry: ON" : "Near Expiry"}
            </Button>
            <Button size="sm" variant={groupBilled ? "default" : "outline"}
                    onClick={() => setGroupBilled(v => !v)}
                    className={groupBilled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"}
                    data-testid="rp-group-billed-btn"
                    title="Group returns by Billed To name — one sale return voucher per name">
              <Users className="w-4 h-4 mr-1" /> {groupBilled ? "Grouped by Billed To" : "Group by Billed To"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="rp-bulk-upload-btn">
              <Package className="w-4 h-4 mr-1" /> Bulk Upload
            </Button>
            <Button size="sm" variant="outline" onClick={exportXlsx}
                    data-testid="rp-export">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            <Button size="sm" variant="outline" onClick={screenPrint}
                    data-testid="rp-screen-print">
              <Printer className="w-4 h-4 mr-1" /> Screen Print
            </Button>
            <div className="ml-auto text-xs text-slate-500 flex items-center gap-2">
              <span data-testid="rp-count">{total} record{total === 1 ? '' : 's'}</span>
              <Button size="sm" variant="ghost" onClick={() => { fetchStats(); fetchRows(); }}
                      className="h-7 w-7 p-0" data-testid="rp-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          <ReturnValueStrip />
          <DbaScanDialog open={scanOpen} onClose={closeScan} onCode={fetchParcel} />
          <DbaScanDialog open={batch.open} onClose={closeBatch} onCode={batchCode} continuous resetKey={batch.reason} title="Batch scan — returned parcels"
            top={<div className="space-y-1.5" data-testid="rp-batch-panel">
              <div className="text-[11px] font-semibold text-slate-600">Reason for the whole pile:</div>
              <div className="flex flex-wrap gap-1">
                {REASONS.map(rs => (
                  <button key={rs} type="button" onClick={() => setBatch(b => ({ ...b, reason: rs }))}
                    data-testid={`rp-batch-reason-${rs.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${batch.reason === rs ? "border-amber-600 bg-amber-600 text-white" : "border-amber-300 bg-white text-amber-800"}`}>{rs}</button>))}
              </div>
              <div className="text-[11px] text-slate-600" data-testid="rp-batch-count">
                Saved this round: <b>{batch.saved.length}</b>{batch.saved.length ? <span className="text-slate-400"> · last {batch.saved.slice(0, 3).join(", ")}</span> : null}
              </div>
            </div>} />
          <RpMobileCards rows={rows} loading={loading} statusLabel={statusLabel} onReship={openReship}
            onArchive={(r, v) => { setVoucherDialog({ row: r, targetStatus: v }); setVoucherInput(""); }} />
          <div className="border rounded-md overflow-x-auto hidden md:block">
            <table className="w-full text-xs border-collapse" data-testid="rp-table">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('return_date')}
                      data-testid="rp-sort-return-date">
                    Return Date{sortArrow('return_date')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('invoice_date')}
                      data-testid="rp-sort-invoice-date">
                    Invoice Date{sortArrow('invoice_date')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('tracking')}
                      data-testid="rp-sort-tracking">
                    Tracking{sortArrow('tracking')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('invoice_no')}
                      data-testid="rp-sort-invoice-no">
                    Invoice #{sortArrow('invoice_no')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('client_code')} data-testid="rp-sort-client-code">
                    Client Code{sortArrow('client_code')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('customer_name')}
                      data-testid="rp-sort-customer">
                    Customer{sortArrow('customer_name')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('country')} data-testid="rp-sort-country">
                    Country{sortArrow('country')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200" title="Billed-to / invoice name from the packing slip — use it for the sale return voucher"
                      onClick={() => handleSortClick('billed_to')} data-testid="rp-sort-billed-to">
                    Billed To{sortArrow('billed_to')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('service')} data-testid="rp-sort-service">
                    Service{sortArrow('service')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('products')} data-testid="rp-sort-products">
                    Products{sortArrow('products')}
                  </th>
                  <th className="p-2 text-right border border-slate-200 whitespace-nowrap cursor-pointer select-none hover:bg-slate-200" title="Sale price per unit — packing slip of the invoice, else Order Register snapshot (INR)"
                      onClick={() => handleSortClick('unit_inr')} data-testid="rp-sort-unit-inr">
                    Unit ₹{sortArrow('unit_inr')}
                  </th>
                  <th className="p-2 text-right border border-slate-200 whitespace-nowrap cursor-pointer select-none hover:bg-slate-200" title="Sale price per unit — packing slip of the invoice, else Order Register snapshot (USD)"
                      onClick={() => handleSortClick('unit_usd')} data-testid="rp-sort-unit-usd">
                    Unit ${sortArrow('unit_usd')}
                  </th>
                  <th className="p-2 text-right border border-slate-200 whitespace-nowrap cursor-pointer select-none hover:bg-slate-200" title="Parcel return value = Σ qty × unit price"
                      onClick={() => handleSortClick('value')} data-testid="rp-sort-value">
                    Value ₹ / ${sortArrow('value')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('batch')} data-testid="rp-sort-batch">
                    Batch{sortArrow('batch')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('expiry')}
                      data-testid="rp-sort-expiry">
                    Expiry{sortArrow('expiry')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('reason')} data-testid="rp-sort-reason">
                    Reason{sortArrow('reason')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200 w-40"
                      onClick={() => handleSortClick('status')} data-testid="rp-sort-status">
                    Status{sortArrow('status')}
                  </th>
                  <th className="p-2 text-left border border-slate-200 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => handleSortClick('remarks')} data-testid="rp-sort-remarks">
                    Remarks{sortArrow('remarks')}
                  </th>
                  {isAdmin && <th className="p-2 text-right border border-slate-200 w-16">Del</th>}
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 19 : 18} className="text-center text-slate-500 py-6">
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 19 : 18} className="text-center text-slate-500 py-6"
                          data-testid="rp-empty">
                    No returned parcels yet. Enter a tracking number above to register one.
                  </td></tr>
                ) : (() => {
                  const bt = (r) => (r.billed_to || '').trim();
                  const view = groupBilled
                    ? [...rows].sort((a, b) => (!bt(a)) - (!bt(b)) || bt(a).localeCompare(bt(b), undefined, { sensitivity: 'base' }))
                    : rows;
                  const stats = {};
                  view.forEach(r => {
                    const k = bt(r).toUpperCase();
                    stats[k] = stats[k] || { parcels: 0, qty: 0, rows: [] };
                    stats[k].parcels += 1;
                    stats[k].rows.push(r);
                    stats[k].qty += (r.products || []).reduce((s, p) => s + (Number(p.qty) || 0), 0) || Number(r.total_qty) || 0;
                  });
                  return view.map((r, gIdx) => {
                  const products = r.products?.length ? r.products : [{ name: '—', qty: r.total_qty || 0 }];
                  const gk = bt(r).toUpperCase();
                  const groupHead = groupBilled && (gIdx === 0 || bt(view[gIdx - 1]).toUpperCase() !== gk)
                    ? <BilledToGroupRow key={`g-${r.id}`} name={bt(r)} parcels={stats[gk].parcels} qty={stats[gk].qty} rows={stats[gk].rows} colSpan={isAdmin ? 19 : 18} />
                    : null;
                  const groupTint = gIdx % 2 === 0 ? '' : 'bg-amber-50/40';
                  return [groupHead, ...products.map((p, pIdx) => {
                    const first = pIdx === 0;
                    const topBorder = first ? 'border-t-2 border-t-slate-400' : '';
                    return (
                      <tr key={`${r.id}-${pIdx}`}
                          className={`${groupTint} ${topBorder}`}
                          data-testid={`rp-row-${r.id}-${pIdx}`}>
                        <td className="p-1.5 whitespace-nowrap border border-slate-200 font-semibold">
                          {first ? r.return_date : ''}
                        </td>
                        <td className="p-1.5 whitespace-nowrap border border-slate-200 text-slate-700">
                          {first ? (r.shipping_date || '').split('T')[0] : ''}
                        </td>
                        <td className="p-1.5 font-mono text-[10px] border border-slate-200">
                          {first ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">
                              {r.tracking}
                            </span>
                          ) : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200 font-semibold text-slate-700">
                          {first ? (r.invoice_no || '—') : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200">
                          {first ? r.client_code : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200">
                          {first ? r.customer_name : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200 text-slate-700" data-testid={`rp-country-${r.id}`}>
                          {first ? (r.destination_country || '—') : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200" data-testid={`rp-billed-to-${r.id}`}>
                          {first && <BilledToCell row={r} onSave={(v) => inlineUpdate(r, { billed_to: v })} />}
                        </td>
                        <td className="p-1.5 border border-slate-200">
                          {first && r.service ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold text-[10px]">
                              {r.service}
                            </span>
                          ) : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200">
                          <span className="font-medium">{p.name}</span>
                          <span className="text-slate-500"> ×{p.qty || 0}</span>
                        </td>
                        <td className="p-1.5 border border-slate-200 text-right font-semibold text-slate-800 whitespace-nowrap" data-testid={`rp-price-inr-${r.id}-${pIdx}`} title={p.price_source || ""}>
                          {p.price_inr != null ? `₹${Number(p.price_inr).toFixed(2)}` : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200 text-right font-semibold text-emerald-700 whitespace-nowrap" data-testid={`rp-price-usd-${r.id}-${pIdx}`} title={p.price_source || ""}>
                          {p.price_usd != null ? `$${Number(p.price_usd).toFixed(2)}` : ''}
                          {p.price_source && <div className="text-[9px] font-medium text-slate-500 leading-tight" data-testid={`rp-price-src-${r.id}-${pIdx}`}>{p.price_source.startsWith("Packing") ? "slip" : p.price_source.startsWith("OR") ? "OR" : "alias ₹"}</div>}
                        </td>
                        <td className="p-1.5 border border-slate-200 text-right whitespace-nowrap" data-testid={first ? `rp-value-${r.id}` : undefined}>
                          {first && r.value_usd != null ? (
                            <span title={r.value_partial ? "Some products have no invoice price" : ""}>
                              <b>₹{Number(r.value_inr).toFixed(2)}</b><br /><span className="text-emerald-700">${Number(r.value_usd).toFixed(2)}</span>
                              {r.value_partial && <span className="ml-1 text-[9px] font-bold text-amber-700">partial</span>}
                            </span>
                          ) : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200 font-mono text-[10px] text-slate-600">
                          {p.batch || ''}
                        </td>
                        <td className={`p-1.5 border border-slate-200 font-mono text-[10px] ${expiryCellClass(p.exp)}`}
                            data-testid={`rp-exp-${r.id}-${pIdx}`}>
                          {p.exp || ''}
                        </td>
                        <td className="p-1.5 border border-slate-200">
                          {first ? r.return_reason : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200 min-w-[180px]">
                          {first ? (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                <Select value={r.status}
                                        onValueChange={(v) => {
                                          if (v === 'Reshipped') { openReship(r); return; }
                                          if (ARCHIVAL_STATUSES.includes(v)) {
                                            setVoucherDialog({ row: r, targetStatus: v });
                                            setVoucherInput("");
                                            return;
                                          }
                                          inlineUpdate(r, { status: v });
                                        }}>
                                  <SelectTrigger className="h-6 text-[10px] flex-1"
                                                 data-testid={`rp-row-status-${r.id}`}>
                                    <SelectValue placeholder="Select…">
                                      {statusLabel(r)}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STATUSES.map(s => (
                                      <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button size="sm" variant="outline"
                                        className="h-6 px-1.5 text-[9px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                        onClick={() => openReship(r)}
                                        title="Link this return to a new outbound tracking"
                                        data-testid={`rp-reship-btn-${r.id}`}>
                                  <Truck className="w-2.5 h-2.5 mr-0.5" /> Reship
                                </Button>
                              </div>
                              {r.status === 'Reshipped' && r.new_tracking && (
                                <div className="flex items-center gap-1 flex-wrap"
                                     data-testid={`rp-reship-link-${r.id}`}>
                                  <span className="text-[9px] text-emerald-700 font-mono leading-tight">
                                    → {r.new_tracking}
                                    {r.new_invoice ? ` • ${r.new_invoice}` : ''}
                                    {r.reship_date ? ` • ${r.reship_date}` : ''}
                                  </span>
                                  <button type="button"
                                          onClick={() => cloneToBulkData(r)}
                                          className="text-[9px] px-1 py-0.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 inline-flex items-center gap-0.5"
                                          title="Copy TSV row + open Bulk Data to paste and finish the reship"
                                          data-testid={`rp-clone-bulk-${r.id}`}>
                                    <Copy className="w-2.5 h-2.5" /> Clone → Bulk Data
                                  </button>
                                  <button type="button"
                                          onClick={() => openHistory(r)}
                                          className="text-[9px] px-1 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-0.5"
                                          title="Show the full original → return → reship timeline"
                                          data-testid={`rp-history-btn-${r.id}`}>
                                    <History className="w-2.5 h-2.5" /> History
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : ''}
                        </td>
                        <td className="p-1.5 border border-slate-200 max-w-[240px] cursor-text hover:bg-amber-50/40"
                            title={first ? "Double-click to edit remarks" : ""}
                            onDoubleClick={first ? () => setEditRow({ ...r }) : undefined}>
                          {first ? (r.remarks || <span className="text-slate-400 italic">— dbl-click —</span>) : ''}
                        </td>
                        {isAdmin && first && (
                          <td className="p-1.5 text-right border border-slate-200" rowSpan={products.length}>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-600"
                                    onClick={() => setConfirmDelete(r)}
                                    data-testid={`rp-delete-${r.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })];
                  });
                })()}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 text-xs">
              <span className="text-slate-500">Page {page} of {totalPages}</span>
              <Button size="sm" variant="outline" className="h-7" disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="rp-prev">
                Prev
              </Button>
              <Button size="sm" variant="outline" className="h-7" disabled={page >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="rp-next">
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duplicate popup */}
      <Dialog open={!!dupPopup} onOpenChange={(o) => !o && setDupPopup(null)}>
        <DialogContent className="max-w-md" data-testid="rp-dup-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Tracking already registered as Returned</DialogTitle>
            <DialogDescription>
              This tracking already exists in Returned Parcels.
            </DialogDescription>
          </DialogHeader>
          {dupPopup?.existing && (
            <div className="text-sm text-slate-700 space-y-1 border rounded p-2 bg-amber-50/60">
              <div><b>Tracking:</b> {dupPopup.tracking}</div>
              <div><b>Status:</b> {dupPopup.existing.status || '—'}</div>
              <div><b>Return Date:</b> {dupPopup.existing.return_date || '—'}</div>
              <div><b>Reason:</b> {dupPopup.existing.return_reason || '—'}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupPopup(null)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => { setQ(dupPopup.tracking); setDupPopup(null); }}
                    data-testid="rp-dup-view">
              View Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation with password guard */}
      <Dialog open={!!confirmDelete}
              onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setDeletePassword(""); } }}>
        <DialogContent className="max-w-md" data-testid="rp-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Delete returned parcel?</DialogTitle>
            <DialogDescription>
              This will permanently remove the return record. Enter the admin
              password to confirm.
            </DialogDescription>
          </DialogHeader>
          {confirmDelete && (
            <div className="text-sm text-slate-700 border rounded p-2 bg-rose-50/60">
              <div><b>Tracking:</b> {confirmDelete.tracking}</div>
              <div><b>Customer:</b> {confirmDelete.customer_name}</div>
            </div>
          )}
          <Input type="password" value={deletePassword}
                 onChange={(e) => setDeletePassword(e.target.value)}
                 placeholder="Admin password"
                 data-testid="rp-delete-password" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConfirmDelete(null); setDeletePassword(""); }}>
              Cancel
            </Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={doDelete} disabled={deleting || !deletePassword}
                    data-testid="rp-confirm-delete">
              {deleting ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Deleting…</> :
                          <><Trash2 className="w-3 h-3 mr-1" /> Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline remarks edit dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-lg" data-testid="rp-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Return · {editRow?.tracking}</DialogTitle>
          </DialogHeader>
          {editRow && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Return Reason</Label>
                <Select value={editRow.return_reason || "__none__"}
                        onValueChange={(v) => setEditRow(r => ({ ...r, return_reason: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— select —</SelectItem>
                    {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Remarks</Label>
                <Input value={editRow.remarks || ""}
                       onChange={(e) => setEditRow(r => ({ ...r, remarks: e.target.value }))}
                       className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editRow.status}
                        onValueChange={(v) => setEditRow(r => ({ ...r, status: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue>
                    {STATUS_LABELS[editRow.status] || editRow.status}
                  </SelectValue></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Iter 162 — Voucher # is REQUIRED when the target status is
                  Marg OK / Damage / Empty Parcel. Field appears inline so
                  ops can complete the flow in a single dialog. */}
              {ARCHIVAL_STATUSES.includes(editRow.status) && (
                <div className="border rounded-md p-2 bg-emerald-50/60 border-emerald-200"
                     data-testid="rp-edit-voucher-inline">
                  <Label className="text-xs">
                    Voucher # <span className="text-rose-600">*</span>
                    <span className="text-slate-500 font-normal ml-1">
                      — status will display as{' '}
                      <span className="font-mono font-semibold">
                        {STATUS_PREFIX[editRow.status]}-{(editRow.voucher_no || 'XXXX').trim() || 'XXXX'}
                      </span>
                    </span>
                  </Label>
                  <Input value={editRow.voucher_no || ""}
                         onChange={(e) => setEditRow(r => ({ ...r, voucher_no: e.target.value }))}
                         placeholder="e.g. 2345"
                         autoFocus
                         className="h-8 text-xs font-mono"
                         data-testid="rp-edit-voucher-input" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={async () => {
                      // Iter 162/163 — route archival changes through
                      // /archive and add password guard when the row is
                      // already archived.
                      const isArchival = ARCHIVAL_STATUSES.includes(editRow.status);
                      const voucher = (editRow.voucher_no || "").trim();
                      if (isArchival && !voucher) {
                        toast.error("Voucher # is required for Marg OK / Damage / Empty Parcel");
                        return;
                      }
                      const doSave = async (pwd = "") => {
                        const patch = {
                          remarks: editRow.remarks,
                          return_reason: editRow.return_reason,
                        };
                        if (!isArchival) patch.status = editRow.status;
                        const ok = await inlineUpdate(editRow, patch, pwd);
                        if (!ok) return false;   // 423 already prompted
                        if (isArchival) {
                          try {
                            const body = { status: editRow.status, voucher_no: voucher };
                            if (pwd) body.password = pwd;
                            const r = await axios.post(
                              `${API}/returned-parcels/${editRow.id}/archive`,
                              body, { headers: getAuthHeader() });
                            const lbl = statusLabel(r.data);
                            toast.success(`Archived — ${lbl}`);
                            fetchStats(); fetchArchiveStats(); fetchRows();
                          } catch (e) {
                            if (e.response?.status === 423) {
                              setArchivedPwdPrompt({
                                verb: 'change voucher',
                                retry: (pwd2) => doSave(pwd2),
                              });
                              return false;
                            }
                            toast.error(e.response?.data?.detail || "Archive failed");
                            return false;
                          }
                        }
                        setEditRow(null);
                        return true;
                      };
                      await doSave();
                    }}
                    data-testid="rp-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 157 — Bulk Return Upload */}
      <Dialog open={bulkOpen} onOpenChange={(o) => {
        setBulkOpen(o); if (!o) { setBulkResult(null); setBulkText(""); }
      }}>
        <DialogContent className="max-w-2xl" data-testid="rp-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-4 h-4 text-indigo-600" /> Bulk Return Upload
            </DialogTitle>
            <DialogDescription>
              Paste tracking numbers (one per line) or upload a CSV. Each tracking is
              looked up in Database A (last 12 months) and registered with the common
              return date and reason chosen below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 pb-1">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Return Date *</Label>
                <Input type="date" value={bulkDate}
                       onChange={(e) => setBulkDate(e.target.value)}
                       data-testid="rp-bulk-date" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Return Reason *</Label>
                <Select value={bulkReason || "__none__"}
                        onValueChange={(v) => setBulkReason(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="h-9" data-testid="rp-bulk-reason">
                    <SelectValue placeholder="— select —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— select —</SelectItem>
                    {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Remarks (applied to all)</Label>
              <Input value={bulkRemarks}
                     onChange={(e) => setBulkRemarks(e.target.value)}
                     placeholder="Optional common notes"
                     data-testid="rp-bulk-remarks" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Tracking Numbers (one per line)</Label>
                <label className="text-[11px] text-indigo-600 hover:underline cursor-pointer">
                  <input type="file" accept=".csv,.txt" className="hidden"
                         onChange={(e) => onCsvUpload(e.target.files?.[0])}
                         data-testid="rp-bulk-file" />
                  or upload CSV…
                </label>
              </div>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={7}
                className="w-full mt-1 rounded border border-slate-300 p-2 font-mono text-[11px]"
                placeholder="LP084629929IN&#10;EMI58321400&#10;…"
                data-testid="rp-bulk-textarea"
              />
              <div className="text-[10px] text-slate-500 mt-1">
                {(() => {
                  const n = bulkText.split(/[\r\n,;\t]+/).filter(Boolean).length;
                  return `${n} tracking${n === 1 ? '' : 's'} ready`;
                })()}
              </div>
            </div>

            {bulkResult && (
              <div className="grid grid-cols-3 gap-2 text-[11px] rounded border border-slate-200 p-2 bg-slate-50"
                   data-testid="rp-bulk-result">
                <div>
                  <div className="text-emerald-700 font-semibold">Created ({(bulkResult.created || []).length})</div>
                  <div className="font-mono text-[10px] max-h-24 overflow-auto">
                    {(bulkResult.created || []).map(t => <div key={t}>{t}</div>)}
                  </div>
                </div>
                <div>
                  <div className="text-amber-700 font-semibold">Duplicates ({(bulkResult.duplicates || []).length})</div>
                  <div className="font-mono text-[10px] max-h-24 overflow-auto">
                    {(bulkResult.duplicates || []).map(t => <div key={t}>{t}</div>)}
                  </div>
                </div>
                <div>
                  <div className="text-rose-700 font-semibold">Not Found ({(bulkResult.not_found || []).length})</div>
                  <div className="font-mono text-[10px] max-h-24 overflow-auto">
                    {(bulkResult.not_found || []).map(t => <div key={t}>{t}</div>)}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}
                    data-testid="rp-bulk-cancel">Close</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={runBulkUpload}
                    disabled={bulkRunning || !bulkText.trim() || !bulkReason}
                    data-testid="rp-bulk-submit">
              {bulkRunning ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Registering…</> :
                              <><Package className="w-4 h-4 mr-1" /> Register All</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 157 — Reship link dialog */}
      <Dialog open={!!reshipRow} onOpenChange={(o) => !o && setReshipRow(null)}>
        <DialogContent className="max-w-md" data-testid="rp-reship-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-emerald-600" /> Link Reshipment
            </DialogTitle>
            <DialogDescription>
              Move this return to <b>Reshipped</b> and link it to the new outbound parcel.
            </DialogDescription>
          </DialogHeader>
          {reshipRow && (
            <div className="text-[11px] text-slate-500 -mt-1">
              Original: <span className="font-mono">{reshipRow.tracking}</span>
              {reshipRow.invoice_no ? <> · Invoice <span className="font-mono">{reshipRow.invoice_no}</span></> : null}
              {reshipRow.customer_name ? <> · {reshipRow.customer_name}</> : null}
            </div>
          )}
          <div className="grid gap-3 py-1">
            <div>
              <Label className="text-xs">New Tracking *</Label>
              <Input value={reshipTracking}
                     onChange={(e) => setReshipTracking(e.target.value.toUpperCase())}
                     placeholder="e.g. LP084712233IN"
                     className="uppercase font-mono"
                     data-testid="rp-reship-tracking" />
            </div>
            <div>
              <Label className="text-xs">New Invoice # *</Label>
              <Input value={reshipInvoice}
                     onChange={(e) => setReshipInvoice(e.target.value.toUpperCase())}
                     placeholder="e.g. IN-2026-004987"
                     className="uppercase font-mono"
                     data-testid="rp-reship-invoice" />
            </div>
            <div>
              <Label className="text-xs">Reship Date</Label>
              <Input type="date" value={reshipDate}
                     onChange={(e) => setReshipDate(e.target.value)}
                     data-testid="rp-reship-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReshipRow(null)}
                    data-testid="rp-reship-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={submitReship}
                    disabled={reshipSaving || !reshipTracking.trim() || !reshipInvoice.trim()}
                    data-testid="rp-reship-submit">
              {reshipSaving ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Linking…</> :
                              <><Truck className="w-4 h-4 mr-1" /> Mark Reshipped</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 160 — Reship History timeline */}
      <Dialog open={!!historyRow} onOpenChange={(o) => !o && setHistoryRow(null)}>
        <DialogContent className="max-w-2xl" data-testid="rp-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-600" />
              Reship History
              {historyRow && (
                <span className="text-[11px] font-normal text-slate-500 ml-1">
                  · {historyRow.customer_name || historyRow.client_code}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Full original → return → reship chain for this parcel
              plus any related returns from the same customer / client.
            </DialogDescription>
          </DialogHeader>

          {historyLoading && (
            <div className="text-center py-10 text-slate-500 text-xs"
                 data-testid="rp-history-loading">
              <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
              Loading timeline…
            </div>
          )}

          {historyData && (
            <>
              <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4 mt-2"
                  data-testid="rp-history-timeline">
                {historyData.events.map((ev, i) => {
                  const dot = ev.kind === 'dispatch' ? 'bg-slate-400'
                    : ev.kind === 'return' ? 'bg-rose-500'
                    : 'bg-emerald-500';
                  const pill = ev.kind === 'dispatch' ? 'bg-slate-100 text-slate-700'
                    : ev.kind === 'return' ? 'bg-rose-100 text-rose-800'
                    : 'bg-emerald-100 text-emerald-800';
                  return (
                    <li key={i} className="ml-4"
                        data-testid={`rp-history-event-${ev.kind}`}>
                      <span className={`absolute -left-[9px] w-4 h-4 rounded-full ${dot} border-2 border-white`} />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${pill}`}>
                          {ev.label}
                        </span>
                        <span className="text-xs text-slate-600">{ev.date || '—'}</span>
                        {ev.tracking && (
                          <span className="font-mono text-[11px] px-1.5 py-0.5 bg-slate-100 rounded">
                            {ev.tracking}
                          </span>
                        )}
                        {ev.invoice_no && (
                          <span className="font-mono text-[10px] text-slate-500">{ev.invoice_no}</span>
                        )}
                        {ev.service && (
                          <span className="text-[10px] px-1 rounded bg-indigo-50 text-indigo-700 font-semibold">
                            {ev.service}
                          </span>
                        )}
                        {ev.destination && (
                          <span className="text-[10px] text-slate-500">→ {ev.destination}</span>
                        )}
                      </div>
                      {ev.kind === 'return' && (
                        <div className="text-[11px] text-slate-600 mt-1">
                          {ev.reason && (
                            <span className="mr-2"><b>Reason:</b> {ev.reason}</span>
                          )}
                          {ev.received_by && (
                            <span className="mr-2"><b>By:</b> {ev.received_by}</span>
                          )}
                          {ev.remarks && (
                            <span className="italic">"{ev.remarks}"</span>
                          )}
                        </div>
                      )}
                      {ev.kind === 'reship' && ev.notes && (
                        <div className="text-[10px] text-amber-700 mt-1">
                          ⚠ {ev.notes}
                        </div>
                      )}
                      {ev.products?.length > 0 && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          {ev.products.map((p, pi) => (
                            <span key={pi} className="mr-2">
                              {p.name || p} <span className="text-slate-400">×{p.qty || 1}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>

              {historyData.related?.length > 0 && (
                <div className="mt-3 border-t pt-2" data-testid="rp-history-related">
                  <div className="text-[11px] font-semibold text-slate-700 mb-1">
                    Other returns from this customer/client
                    <span className="ml-1 text-slate-400 font-normal">
                      ({historyData.related.length})
                    </span>
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    <table className="w-full text-[10px] border-collapse">
                      <thead>
                        <tr className="text-slate-500 uppercase text-[9px]">
                          <th className="text-left p-1 border-b">Date</th>
                          <th className="text-left p-1 border-b">Tracking</th>
                          <th className="text-left p-1 border-b">Reason</th>
                          <th className="text-left p-1 border-b">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.related.map(r => (
                          <tr key={r.id} className="border-b border-slate-100">
                            <td className="p-1">{r.date}</td>
                            <td className="p-1 font-mono">{r.tracking}</td>
                            <td className="p-1">{r.reason}</td>
                            <td className="p-1">
                              <span className="px-1 py-0.5 rounded bg-slate-100">{r.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setHistoryRow(null)}
                    data-testid="rp-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 161 — Voucher # popup for Marg-OK / Damage / Empty Parcel */}
      <Dialog open={!!voucherDialog} onOpenChange={(o) => !o && setVoucherDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="rp-voucher-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              {voucherDialog && `${STATUS_LABELS[voucherDialog.targetStatus]} — Voucher #`}
            </DialogTitle>
            <DialogDescription>
              Enter the Return Voucher Number. The parcel will move to Archive
              and its status will show{' '}
              <span className="font-mono font-semibold">
                {voucherDialog && STATUS_PREFIX[voucherDialog.targetStatus]}-&lt;VR&gt;
              </span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs">VR Number *</Label>
            <Input value={voucherInput}
                   onChange={(e) => setVoucherInput(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') submitVoucher(); }}
                   placeholder="e.g. 2345"
                   autoFocus
                   className="font-mono"
                   data-testid="rp-voucher-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoucherDialog(null)}
                    data-testid="rp-voucher-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => submitVoucher()}
                    disabled={voucherSaving || !voucherInput.trim()}
                    data-testid="rp-voucher-save">
              {voucherSaving ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Saving…</> :
                                <><CheckCircle2 className="w-4 h-4 mr-1" /> Save & Archive</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 163 — Password gate for editing archived rows */}
      <ArchivedPwdDialog
        prompt={archivedPwdPrompt}
        onClose={() => setArchivedPwdPrompt(null)}
      />
    </div>
  );
}

/* Iter 163 — small controlled dialog that asks the operator to
 * re-enter their own login password before an archived row's status,
 * voucher, or archive flag can be changed. */
function ArchivedPwdDialog({ prompt, onClose }) {
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (!prompt) setPwd(""); }, [prompt]);
  if (!prompt) return null;
  const submit = async () => {
    if (!pwd.trim()) { toast.error("Enter your login password"); return; }
    setSubmitting(true);
    try {
      const ok = await prompt.retry(pwd);
      if (ok !== false) onClose();
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="rp-archived-pwd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-amber-600" />
            Password required to {prompt.verb}
          </DialogTitle>
          <DialogDescription>
            This parcel is archived. Enter <b>your team login password</b>{' '}
            to make changes.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-xs">Login Password</Label>
          <Input type="password" value={pwd}
                 onChange={(e) => setPwd(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                 autoFocus
                 data-testid="rp-archived-pwd-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}
                  data-testid="rp-archived-pwd-cancel">Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700"
                  onClick={submit}
                  disabled={submitting || !pwd.trim()}
                  data-testid="rp-archived-pwd-submit">
            {submitting ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Verifying…</> :
                          <>Confirm</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
